import { skipToken, useMutation } from '@apollo/client'
import { Check, Loader } from 'lucide-react'
import { Suspense, useEffect, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { ChangeDefaultRoleSheetFallback } from '@/routes/single-sign-on/jit/change-default-role-sheet/fallback'
import type {
  JitSsoQuery,
  JitSsoQueryVariables,
  UpdateJitDefaultRoleMutation,
  UpdateJitDefaultRoleMutationVariables,
} from '@/routes/single-sign-on/jit/change-default-role-sheet/index.types.gen'

const JIT_SSO_QUERY = gql`
  query JitSso($idCompany: String!) {
    company(id: $idCompany) {
      id
      samlSetting @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
    }
  }
`

const UPDATE_JIT_DEFAULT_ROLE_MUTATION = gql`
  mutation UpdateJitDefaultRole($idCompany: String!, $idCompanyRole: String) {
    updateJitDefaultRole(idCompany: $idCompany, idCompanyRole: $idCompanyRole) {
      id
      idRole
      type
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const COMPANY_ROLES = [
  {
    id: 'company-developer',
    name: 'Company developer',
    description: 'Can view company details and manage assigned services',
  },
  {
    id: 'company-billing',
    name: 'Company billing',
    description:
      'Can view company details, billing info, and manage payment methods',
  },
  {
    id: 'company-admin',
    name: 'Company admin',
    description: 'Can manage all company settings, users, and services',
  },
]

const Content = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()
  const [selectedRole, setSelectedRole] = useState<undefined | string>()

  const { data } = useSuspenseQuery<JitSsoQuery, JitSsoQueryVariables>(
    JIT_SSO_QUERY,
    isOpen ? { variables: { idCompany } } : skipToken
  )

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const defaultRoles =
    liveSamlSetting?.jitDefaultRoles ?? draftSamlSetting?.jitDefaultRoles ?? []
  const companyLevelDefaultRole = defaultRoles.find(
    (role) => role.type === 'company'
  )

  const [updateJitDefaultRole] = useMutation<
    UpdateJitDefaultRoleMutation,
    UpdateJitDefaultRoleMutationVariables
  >(UPDATE_JIT_DEFAULT_ROLE_MUTATION)

  useEffect(() => {
    if (selectedRole === undefined) {
      setSelectedRole(companyLevelDefaultRole?.idRole ?? 'company-developer')
    }
  }, [companyLevelDefaultRole?.idRole, data, selectedRole])

  const onSubmit = async () => {
    startTransition(async () => {
      try {
        await updateJitDefaultRole({
          variables: {
            idCompany,
            idCompanyRole: selectedRole ?? 'company-developer',
          },
        })
        toast(
          <ToasterContent variant="success">
            Default role updated successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to update default role
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Change default role</SheetTitle>
        <SheetDescription>
          Users who join this company account in a self-served way will be
          assigned the default role you set here. You can still configure
          different access levels for each user after they've joined.
        </SheetDescription>
      </SheetHeader>
      <div
        className="flex-1 overflow-y-auto px-4 space-y-2"
        role="radiogroup"
        aria-label="Company roles"
      >
        {COMPANY_ROLES.map((role) => (
          <div
            key={role.id}
            role="radio"
            aria-checked={selectedRole === role.id}
            tabIndex={0}
            onClick={() => setSelectedRole(role.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setSelectedRole(role.id)
              }
            }}
            className={cn(
              'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
              selectedRole === role.id
                ? 'border-primary bg-primary/5'
                : 'hover:bg-muted/50'
            )}
          >
            <div
              className={cn(
                'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 mt-0.5',
                selectedRole === role.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/50'
              )}
            >
              {selectedRole === role.id && <Check className="h-3 w-3" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{role.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {role.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      <SheetFooter className="sticky">
        <Button disabled={isPending} onClick={() => onSubmit()}>
          {isPending ? <Loader className="animate-spin" /> : null}
          Save changes
        </Button>
      </SheetFooter>
    </>
  )
}

export const ChangeDefaultRoleSheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]" onEscapeKeyDown={onClose}>
        <Suspense fallback={<ChangeDefaultRoleSheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
